import { twMerge } from 'tailwind-merge';
import { type UndecoratedFinancialReport } from '@flemingmetrics/api-client';
import { OptionsCell, TableCell, TableRow } from '@/components/Table';
import { renderDate } from '@/utils/time';
import { formatNumber, formatPercentage, renderPrice } from '@/utils/strings';
import { useToggle } from '@/hooks/useToggle';
import DeleteInvestmentDialog from '@/dialogs/DeleteInvestmentDialog';
import { useInvestment } from '@/hooks/useStore';
import InvestmentDialog from '@/dialogs/InvestmentDialog';
import { classes, styles } from '../utils';
import { getSummary } from './utils';

export type InvestmentRowProps = {
  companyId: number;
  investorId: number;
  investmentId?: number;
  latestFinancialReport?: UndecoratedFinancialReport;
  onSave?: () => void;
  onCancel?: () => void;
};

export default function InvestmentRow({
  investmentId,
  latestFinancialReport,
}: InvestmentRowProps) {
  const investment = useInvestment(investmentId);
  const [isEditing, toggleEditing] = useToggle(!investmentId);
  const [isDeleting, toggleDeleting] = useToggle(false);

  if (!investment) return null; // TODO: Handle this better?

  const summary = getSummary(investment, latestFinancialReport);
  const { deltaPrice, deltaPercent } = summary.profitLoss;

  const renderDateCell = () => {
    return (
      <TableCell className={classes.cell} align="left">
        {investment?.date ? renderDate(investment.date) : '--'}
      </TableCell>
    );
  };

  const renderSharePriceCell = () => {
    return (
      <TableCell className={classes.cell} align="right">
        {renderPrice(summary.sharePrice)}
      </TableCell>
    );
    // }
  };

  const renderSharesCell = () => {
    return (
      <TableCell className={classes.cell} align="right">
        {investment ? formatNumber(investment.shareCount) : '--'}
      </TableCell>
    );
  };

  const renderCurrentPriceCell = () => {
    return (
      <TableCell className={classes.cell} align="right">
        {isNaN(summary.totalInvested)
          ? '--'
          : renderPrice(summary.totalInvested)}
      </TableCell>
    );
  };

  const renderCurrentSharePriceCell = () => {
    return (
      <TableCell className={classes.cell} align="right">
        {renderPrice(summary.currentSharePrice)}
      </TableCell>
    );
  };

  const renderCurrentValueCell = () => {
    return (
      <TableCell className={classes.cell} align="right">
        {isNaN(summary.currentValue) ? '--' : renderPrice(summary.currentValue)}
      </TableCell>
    );
  };

  const renderProfitLossCell = () => {
    return (
      <TableCell className={classes.cell} align="right">
        {isNaN(deltaPrice) || !isFinite(deltaPercent) ? (
          '--'
        ) : (
          <>
            <span className={classes.price}>{renderPrice(deltaPrice)}</span>
            <span
              className={twMerge(
                classes.percentage,
                deltaPercent > 0 ? classes.positive : classes.negative
              )}>
              ({formatPercentage(deltaPercent, 1)})
            </span>
          </>
        )}
      </TableCell>
    );
  };

  return (
    <>
      {isEditing && (
        <InvestmentDialog
          investmentId={investment.id}
          onClose={() => toggleEditing(false)}
        />
      )}
      {isDeleting && (
        <DeleteInvestmentDialog
          investmentId={investment.id}
          onClose={() => toggleDeleting(false)}
        />
      )}
      <TableRow key={investment.id} sx={styles.row}>
        <TableCell />
        {renderDateCell()}
        {renderSharesCell()}
        {renderSharePriceCell()}
        {renderCurrentPriceCell()}
        {renderCurrentSharePriceCell()}
        {renderCurrentValueCell()}
        {renderProfitLossCell()}
        <OptionsCell
          className={classes.optionsCell}
          onEdit={() => toggleEditing()}
          onDelete={() => toggleDeleting()}
        />
      </TableRow>
    </>
  );
}
